import { useEffect } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { X, ArrowLeft, Send } from 'lucide-react'
import { Button } from '@/components/ui/Button'

interface Photo {
  id: string
  photoTitle: string
  name: string
  grade: string
  photoUrl: string
  thumbnailUrl?: string
  lightboxUrl?: string
}

interface Selections {
  first: string | null
  second: string | null
  third: string | null
}

interface VoteConfirmationModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  photos: Photo[]
  selections: Selections
  isSubmitting: boolean
}

export function VoteConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  photos,
  selections,
  isSubmitting,
}: VoteConfirmationModalProps) {
  // Get selected photos in order
  const getPhoto = (id: string | null) => photos.find((p) => p.id === id)
  const firstPhoto = getPhoto(selections.first)
  const secondPhoto = getPhoto(selections.second)
  const thirdPhoto = getPhoto(selections.third)

  // Keyboard: Escape to close
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const selectedPhotos = [
    { rank: 1, medal: '🥇', label: '1st Place', photo: firstPhoto },
    { rank: 2, medal: '🥈', label: '2nd Place', photo: secondPhoto },
    { rank: 3, medal: '🥉', label: '3rd Place', photo: thirdPhoto },
  ]

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4 backdrop-blur-sm"
          onClick={onClose}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Modal content */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="bg-surface border-border w-full max-w-lg overflow-hidden rounded-2xl border shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="border-border border-b px-6 py-5">
              <h2 className="text-foreground font-display text-center text-xl font-bold">Confirm Your Votes</h2>
              <p className="text-foreground-muted mt-1 text-center text-sm">Review your selections before submitting</p>
            </div>

            {/* Selected photos */}
            <div className="space-y-4 px-6 py-5">
              {selectedPhotos.map(({ rank, medal, label, photo }) => (
                <div key={rank} className="bg-surface-hover flex items-center gap-4 rounded-xl p-3">
                  {/* Medal */}
                  <div className="text-3xl">{medal}</div>

                  {/* Photo thumbnail */}
                  {photo ? (
                    <div className="bg-background h-16 w-16 shrink-0 overflow-hidden rounded-lg">
                      <img
                        src={photo.thumbnailUrl || photo.photoUrl}
                        alt={photo.photoTitle}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="bg-background h-16 w-16 shrink-0 rounded-lg" />
                  )}

                  {/* Info */}
                  <div className="min-w-0 flex-1">
                    <p className="text-foreground-muted text-xs tracking-wide uppercase">{label}</p>
                    <p className="text-foreground truncate font-medium">{photo?.photoTitle || 'Not selected'}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="border-border flex gap-3 border-t px-6 py-5">
              <Button variant="ghost" size="lg" onClick={onClose} disabled={isSubmitting} className="flex-1">
                <ArrowLeft className="h-5 w-5" />
                Go Back
              </Button>
              <Button size="lg" onClick={onConfirm} isLoading={isSubmitting} disabled={isSubmitting} className="flex-1">
                <Send className="h-5 w-5" />
                Submit Votes
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
